import { useState, useCallback } from 'react';
import { router } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

interface GameLogEntry {
    id: number;
    type: string;
    summary: string;
    user: { id: number; username: string } | null;
    metadata: Record<string, unknown> | null;
    created_at: string;
}

interface PaginatedLogs {
    data: GameLogEntry[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

interface Props {
    logs: PaginatedLogs;
    types: string[];
    filters: {
        type: string;
        search: string;
    };
}

/** Human-readable labels for log types. */
const TYPE_LABELS: Record<string, string> = {
    resources_collected: 'Resources Collected',
    resources_collection_failed: 'Collection Failed',
    adventure_started: 'Adventure Started',
    adventure_completed: 'Adventure Completed',
    adventure_rewards_claimed: 'Rewards Claimed',
    adventure_stage_started: 'Stage Started',
    adventure_stage_completed: 'Stage Completed',
    character_created: 'Character Created',
    character_leveled_up: 'Level Up',
    building_started: 'Building Started',
    building_upgraded: 'Building Upgraded',
    building_cancelled: 'Building Cancelled',
    item_awarded: 'Item Awarded',
    item_crafted: 'Item Crafted',
    item_crafting_started: 'Crafting Started',
    item_recycled: 'Item Recycled',
    hex_claimed: 'Hex Claimed',
    hex_relinquished: 'Hex Relinquished',
};

/** Badge color classes for log types. */
const TYPE_COLORS: Record<string, string> = {
    resources_collected: 'bg-green-600 text-green-100',
    resources_collection_failed: 'bg-red-600 text-red-100',
    adventure_started: 'bg-blue-500 text-blue-100',
    adventure_completed: 'bg-blue-600 text-blue-100',
    adventure_rewards_claimed: 'bg-blue-700 text-blue-100',
    adventure_stage_started: 'bg-cyan-600 text-cyan-100',
    adventure_stage_completed: 'bg-cyan-700 text-cyan-100',
    character_created: 'bg-indigo-600 text-indigo-100',
    character_leveled_up: 'bg-indigo-500 text-indigo-100',
    building_started: 'bg-amber-600 text-amber-100',
    building_upgraded: 'bg-amber-500 text-amber-100',
    building_cancelled: 'bg-amber-700 text-amber-100',
    item_awarded: 'bg-yellow-600 text-yellow-100',
    item_crafted: 'bg-teal-600 text-teal-100',
    item_crafting_started: 'bg-teal-500 text-teal-100',
    item_recycled: 'bg-orange-600 text-orange-100',
    hex_claimed: 'bg-purple-600 text-purple-100',
    hex_relinquished: 'bg-gray-600 text-gray-100',
};

function formatDate(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export default function Index({ logs, types, filters }: Props) {
    const [search, setSearch] = useState(filters.search || '');
    const [typeFilter, setTypeFilter] = useState(filters.type || '');
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const handleFilter = useCallback(() => {
        router.get('/gm/game-log', {
            search: search || undefined,
            type: typeFilter || undefined,
        }, { preserveState: true });
    }, [search, typeFilter]);

    const handleClearFilters = () => {
        setSearch('');
        setTypeFilter('');
        router.get('/gm/game-log', {}, { preserveState: true });
    };

    return (
        <GmLayout title="Game Log" activePath="/gm/game-log">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Game Log</h1>
                    <div className="text-gray-400">
                        {logs.total} entr{logs.total !== 1 ? 'ies' : 'y'}
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search User</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Username or name"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400 caret-gray-900"
                            />
                        </div>

                        <div className="w-56">
                            <label className="block text-sm text-gray-400 mb-1">Type</label>
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white cursor-pointer"
                            >
                                <option value="">All Types</option>
                                {types.map((type) => (
                                    <option key={type} value={type}>
                                        {TYPE_LABELS[type] || type}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors cursor-pointer"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-gray-300 rounded transition-colors cursor-pointer"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Log entries */}
                {logs.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No log entries found.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {logs.data.map((log) => (
                            <div
                                key={log.id}
                                className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden"
                            >
                                <button
                                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                                    className="w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-slate-750 transition-colors cursor-pointer"
                                >
                                    {/* Timestamp */}
                                    <span className="text-xs text-gray-400 w-28 flex-shrink-0">
                                        {formatDate(log.created_at)}
                                    </span>

                                    {/* Type badge */}
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${TYPE_COLORS[log.type] || 'bg-slate-600 text-gray-200'}`}>
                                        {TYPE_LABELS[log.type] || log.type}
                                    </span>

                                    {/* Summary */}
                                    <span className="text-sm text-gray-200 flex-1 truncate">
                                        {log.summary}
                                    </span>

                                    {/* Expand indicator */}
                                    {log.metadata && (
                                        <span className="text-gray-500 text-xs flex-shrink-0">
                                            {expandedId === log.id ? '▼' : '▶'}
                                        </span>
                                    )}
                                </button>

                                {/* Expanded metadata */}
                                {expandedId === log.id && log.metadata && (
                                    <div className="px-4 pb-3 border-t border-slate-700">
                                        <MetadataDisplay metadata={log.metadata} type={log.type} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {logs.last_page > 1 && (
                    <Pagination links={logs.links} />
                )}
            </div>
        </GmLayout>
    );
}

/** Display metadata in a structured way based on log type. */
function MetadataDisplay({ metadata, type }: { metadata: Record<string, unknown>; type: string }) {
    if (type === 'resources_collected' || type === 'resources_collection_failed') {
        return <ResourceMetadata metadata={metadata} type={type} />;
    }

    // Generic metadata display for other types
    return (
        <div className="mt-3">
            <pre className="text-xs text-gray-300 bg-slate-900 rounded p-3 overflow-x-auto">
                {JSON.stringify(metadata, null, 2)}
            </pre>
        </div>
    );
}

/** Structured display for resource collection metadata. */
function ResourceMetadata({ metadata, type }: { metadata: Record<string, unknown>; type: string }) {
    const collected = metadata.collected as Record<string, number> | undefined;
    const resourcesBefore = metadata.resources_before as Record<string, number> | undefined;
    const resourcesAfter = metadata.resources_after as Record<string, number> | undefined;
    const hoursCollected = metadata.hours_collected as number | undefined;
    const hexName = metadata.hex_name as string | undefined;
    const coordinates = metadata.coordinates as { q: number; r: number } | undefined;
    const buildingBonuses = metadata.building_bonuses as Record<string, unknown> | undefined;

    if (type === 'resources_collection_failed') {
        return (
            <div className="mt-3 space-y-2">
                <div className="text-red-400 text-sm">
                    <span className="font-medium">Reason:</span> {metadata.reason as string}
                </div>
                {hexName && (
                    <div className="text-gray-400 text-xs">
                        Hex: {hexName} {coordinates ? `(${coordinates.q}, ${coordinates.r})` : ''}
                    </div>
                )}
            </div>
        );
    }

    const resourceKeys = ['tea', 'wood', 'mystical', 'medicinal', 'edible'];

    return (
        <div className="mt-3 space-y-3">
            {/* Location info */}
            <div className="text-gray-400 text-xs">
                Hex: {hexName} {coordinates ? `(${coordinates.q}, ${coordinates.r})` : ''}
                {hoursCollected !== undefined && ` | ${hoursCollected} hour${hoursCollected !== 1 ? 's' : ''} accumulated`}
            </div>

            {/* Resource table */}
            {collected && (
                <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                        <thead>
                            <tr className="text-gray-400 border-b border-slate-700">
                                <th className="text-left py-1 pr-4"></th>
                                {resourceKeys.map((key) => (
                                    <th key={key} className="text-right py-1 px-2 capitalize">{key}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="text-gray-300">
                            {resourcesBefore && (
                                <tr className="border-b border-slate-700/50">
                                    <td className="py-1 pr-4 text-gray-400">Before</td>
                                    {resourceKeys.map((key) => (
                                        <td key={key} className="text-right py-1 px-2">{resourcesBefore[key] ?? 0}</td>
                                    ))}
                                </tr>
                            )}
                            <tr className="border-b border-slate-700/50">
                                <td className="py-1 pr-4 text-green-400 font-medium">Collected</td>
                                {resourceKeys.map((key) => (
                                    <td key={key} className="text-right py-1 px-2 text-green-400">+{collected[key] ?? 0}</td>
                                ))}
                            </tr>
                            {resourcesAfter && (
                                <tr>
                                    <td className="py-1 pr-4 text-gray-400">After</td>
                                    {resourceKeys.map((key) => (
                                        <td key={key} className="text-right py-1 px-2">{resourcesAfter[key] ?? 0}</td>
                                    ))}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Building bonuses */}
            {buildingBonuses && Object.keys(buildingBonuses).length > 0 && (
                <div className="text-xs text-gray-400">
                    <span className="font-medium">Building bonuses:</span>{' '}
                    {Object.entries(buildingBonuses).map(([key, bonus]) => {
                        const b = bonus as { total_bonus?: number } | null;
                        if (!b?.total_bonus) return null;
                        const label = key.replace(/_/g, ' ');
                        return (
                            <span key={key} className="mr-3">
                                {label}: +{b.total_bonus}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}
